import { AfterViewInit, Component, inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { WorkflowService } from '../../../../../../../../shared/services/workflow.service';
import { FormControl, FormGroup } from '@angular/forms';
import { firstValueFrom, Subject } from 'rxjs';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../../../../../../../state/app.state';
import { CommonService } from '../../../../../../../../shared/services/common.service';
import { ProjectsService } from '../../../../../../core/projects.service';
import { ActivatedRoute, Router } from '@angular/router';
import { debounceTime, filter, finalize, switchMap, take, takeUntil } from 'rxjs/operators';
import {
  currentLocation,
  lineageList,
  projectDetails,
  rfiItemDetails,
  rfiNo,
  usersListInfo,
} from '../../../../../../core/projects.selectors';
import {
  ClearRFIItemDetailsWithId,
  CreateRFIRequest,
  ExecuteWorkflowActionStateRequest,
  FetchLineageRequest,
  FetchRFIsListRequest,
  FetchRFIItemDetailsWithIdRequest,
  FetchRFINoRequest,
  FetchUsersListRequest,
} from '../../../../../../core/projects.actions';
import { GetVideoIdRequest, UploadFileRequest } from '../../../../../../../../shared/core/shared.actions';
import { classifyFile } from 'src/app/shared/helpers';
import * as moment from 'moment/moment';
import { AuthService, ReportService } from '../../../../../../../../shared/services';
import { MatTabChangeEvent, MatTabGroup } from '@angular/material/tabs';
// Direct path: the components barrel pulls in @arcgis/core, which the design-context karma suite can't bundle.
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../../../../../../../shared/components/confirm-dialog/confirm-dialog.component';
import { MatDialog as MatDialog } from '@angular/material/dialog';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { MatSnackBar as MatSnackBar } from '@angular/material/snack-bar';
import { Ability, subject } from '@casl/ability';
import { AnalyticsService } from '../../../../../../../../shared/services/analytics.service';
import { dailyReportLocationConfig } from '../../../../../../../../shared/core/shared.selectors';
import { DrawingsAvailabilityService } from '../../../../../../../../shared/services/drawings-availability.service';
import { DrawingsAnalytics } from '../../drawings/drawings-analytics';
import { LocationPickerLauncherService } from '../../../../../../../../shared/services/location-picker-launcher.service';
import { DailyReportService } from '../../../../../site-details-page/tab-screens/daily-report/daily-report.service';
import {
  StationRangeComponent,
} from '../../../../../site-details-page/tab-screens/daily-report/daily-report-components/detail-components/control-forms-details/station-range/station-range.component';
import {
  hasRemarkLocation,
  normalizeLocationPayload,
  projectHasAlignmentGeometry,
  readRemarkLocation,
  remarkLocationPayload,
  resolveDailyReportLocationMode,
} from '../../../../../site-details-page/tab-screens/daily-diary/daily-diary-remark.util';

interface AddMenuOption {
  key: string;
  icon: string;
  label: string;
  handler: () => void;
}

type LooseRecord = Record<string, unknown>;

@Component({
    selector: 'app-create-or-edit-rfis-item',
    templateUrl: './create-or-edit-rfis-item.component.html',
    styleUrls: ['./create-or-edit-rfis-item.component.scss'],
    standalone: false
})
export class CreateOrEditRfisItemComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('fileInput', { static: false }) fileInputRef;
  @ViewChild('snTabGroup') tabs: MatTabGroup;
  @Input() data: any;
  limits: any;
  errorMessages: any = {};
  errorMessageMap = {
    reviewers: {
      required: 'This field is required.',
    },
    actions_pictures: {
      required: 'This field is required.',
    },
    actions_final_response: {
      required: 'This field is required.',
    },
    actions_select_from_previous_responses: {
      required: 'This field is required.',
    },
    actions_comments: {
      required: 'This field is required.',
    },
    actions_attachments: {
      required: 'This field is required.',
    },
    need_by_date: {
      required: 'This field is required.',
    },
    title: {
      required: 'This field is required.',
    },
    description: {
      required: 'This field is required.',
    },
    actions__fyi_users: {
      required: 'This field is required.',
    },
    actions: {
      required: 'This field is required.',
    },
    actions__reviewers: {
      required: 'This field is required.',
    },
    actions_response_dropdown: {
      required: 'This field is required.',
    },
  };
  form = new FormGroup<any>({
    rfiNo: new FormControl({ value: null, disabled: true }),
    need_by_date: new FormControl({ value: null, disabled: false }),
    title: new FormControl({ value: null, disabled: false }),
    description: new FormControl({ value: null, disabled: false }),
    pictures: new FormControl({ value: '[]', disabled: false }),
    videos: new FormControl({ value: '[]', disabled: false }),
    // Same optional location fields as a diary remark / report activity
    // (see daily-diary-remark.util). Null clears them on the api.
    latitude: new FormControl({ value: '', disabled: false }),
    longitude: new FormControl({ value: '', disabled: false }),
    location: new FormControl({ value: '', disabled: false }),
    station_range: new FormControl({ value: null, disabled: false }),
    // Design context (spec §12). Its presence turns on the location card's link UI and the launcher's catalog
    // check; the payload omits it while it is null and untouched (no layers → today's payload).
    map_feature_link: new FormControl({ value: null, disabled: false }),
  });
  projectId: any;
  isFormDirty: boolean;
  userOptions = [];
  private readonly attachmentsMenuOption: AddMenuOption = {
    key: 'picture',
    icon: 'paperclip',
    label: 'Attachments',
    handler: () => {
      this.fileInputRef.nativeElement.click();
    },
  };
  private readonly locationMenuOption: AddMenuOption = {
    key: 'location',
    icon: 'marker-outline',
    label: 'Add location',
    handler: () => this.openLocationPicker(),
  };
  /** W49: as on mobile, the picker opened on its Drawing tab; offered when the project has published sheets */
  private readonly markOnDrawingMenuOption: AddMenuOption = {
    key: 'mark_on_drawing',
    icon: 'markup-line',
    label: 'Mark on drawing',
    handler: () => {
      this.track.track('mark_on_drawing_clicked', { record_type: 'rfi' });
      void this.openLocationPicker('drawing');
    },
  };
  private readonly stationRangeMenuOption: AddMenuOption = {
    key: 'station_range',
    icon: 'plus',
    label: 'Station range',
    handler: () => this.openStationRangeDialog(),
  };
  addMenuOptions: AddMenuOption[] = [this.attachmentsMenuOption, this.locationMenuOption];

  /**
   * Whether the "+" menu offers "Station range": same gate as diary remarks —
   * the resolved daily-report location mode (project, then owner org, then app
   * config) is `station_range`, OR the project carries alignment geometry.
   */
  stationRangeEnabled = false;
  private project: LooseRecord | null = null;
  private locationConfig: unknown = null;
  mediaFiles = [];
  videoToBeDeletedFromMuxDb = [];
  editable = true;
  selectedTabIndex = 0;
  tabIndexMap = {
    details: 0,
    discussion: 1,
  };
  actionsOptions: any;
  displayNextActions: boolean;
  displayAprovalNextActions: boolean;
  role: any;
  account: any;
  creatorRole: any;
  selectedAction: any = [];
  statusMap = {
    open: {
      label: 'Awaiting approval',
      bgColor: 'bg-submitted',
      textColor: 'text-submitted',
    },
    submitted: {
      label: 'Awaiting approval',
      bgColor: 'bg-submitted',
      textColor: 'text-submitted',
    },
    approved: {
      label: 'Approved',
      bgColor: 'bg-approved',
      textColor: 'txt-approved',
    },
    draft: {
      label: 'Draft',
      bgColor: 'bg-draft',
      textColor: 'txt-draft',
    },
    sent_for_review: {
      label: 'Sent for review',
      bgColor: 'bg-submitted',
      textColor: 'text-submitted',
    },
    closed: {
      label: 'Closed',
      bgColor: 'bg-approved',
      textColor: 'txt-approved',
    },
  };
  finalResponse: any = [];
  responseList: any = [];
  private readonly onDestroy: Subject<any> = new Subject<any>();

  public downloadingReport = false;
  public deletingRfi = false;
  private readonly ability = inject(Ability);
  private readonly dailyReportService = inject(DailyReportService);
  private readonly locationPickerLauncher = inject(LocationPickerLauncherService);
  private readonly drawingsAvailability = inject(DrawingsAvailabilityService);
  private readonly track = inject(DrawingsAnalytics);
  /** W49: "Mark on drawing" in the ＋ menu */
  hasPublishedSheets = false;

  constructor(
    private store: Store<fromRoot.State>,
    private workflowService: WorkflowService,
    private commonService: CommonService,
    private projectsService: ProjectsService,
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    public dialog: MatDialog,
    private analyticsService: AnalyticsService,
    private reportService: ReportService,
    private snackBar: MatSnackBar,
  ) {

    this.route.queryParams
      .pipe(takeUntil(this.onDestroy))
      .subscribe(qp => {
        if (qp?.rifId) {
          this.store.dispatch(FetchRFIItemDetailsWithIdRequest({ payload: { rifId: qp?.rifId } }));
        } else {
          this.store.dispatch(ClearRFIItemDetailsWithId());
        }

        if (qp.subTab) {
          this.selectedTabIndex = this.tabIndexMap[qp.subTab] || 0;
        }
      });

  }

  ngOnInit() {
    const url = this.router.url;
    const path = url.split('?')[0];
    const segments = path.split('/');
    this.projectId = segments[segments.length - 1];
    if (this.projectId) {
      this.drawingsAvailability.available(this.projectId).then(found => {
        this.hasPublishedSheets = found;
        this.updateStationRangeEnabled();
      }).catch(() => undefined);
    }

    if (this.projectId) {
      this.store.dispatch(FetchRFINoRequest({ payload: { projectId: this.projectId } }));
    }

    this.account = this.authService.getCurrentUser();
    this.role = this.account.roles.find(obj => obj.project_id === this.projectId)?.role;

    if (this.role !== 'contractor') {
      this.form.addControl('actions', new FormControl({ value: null, disabled: false }), { emitEvent: false });

      this.form?.controls?.['actions'].valueChanges
        .pipe(debounceTime(200), takeUntil(this.onDestroy))
        .subscribe((value) => {
          Object.keys(this.form.controls)
            .filter(key => key.startsWith('actions_'))
            .forEach(key => this.form.removeControl(key, { emitEvent: false }));

          this.selectedAction = this.actionsOptions?.filter(o => o.state === value);

          this.selectedAction?.forEach(obj => {
            obj?.options?.formInputs?.forEach(o => {
              if (o?.name === 'attachments') {
                this.form.addControl(`actions_pictures`, new FormControl({ value: '[]', disabled: false }), { emitEvent: false });
              }

              if (o?.name === 'final_response') {
                this.form.addControl(`actions_select_from_previous_responses`,
                  new FormControl({
                    value: null,
                    disabled: false,
                  }), { emitEvent: false });


                this.form?.controls?.['actions_select_from_previous_responses']?.valueChanges
                  .pipe(takeUntil(this.onDestroy))
                  .subscribe((v: any) => {
                    if (v) {
                      this.form.addControl(`actions_response_dropdown`, new FormControl({
                        value: null,
                        disabled: false,
                      }), { emitEvent: false });

                      this.form?.controls?.['actions_response_dropdown']?.valueChanges
                        .pipe(takeUntil(this.onDestroy))
                        .subscribe((data: any) => {

                          if (!data) {
                            return;
                          }

                          const selectedResponse = this.responseList.find(r => r?.id === data);
                          if (!selectedResponse) {
                            return;
                          }

                          const newActionAttachments = (selectedResponse.attachments || []).map(a => ({
                            ...a,
                            tags: ['actions-attachments'],
                          }));

                          this.mediaFiles = this.mediaFiles.filter(
                            file => !(file.tags && file.tags.includes('actions-attachments')),
                          );

                          this.mediaFiles = [...this.mediaFiles, ...newActionAttachments];

                          setTimeout(() => {
                            this.projectsService.mediaData.next(this.mediaFiles);
                          });

                          this.form.patchValue({
                              actions_final_response: selectedResponse.response,
                              actions_pictures: JSON.stringify(newActionAttachments),
                            },
                            { emitEvent: false },
                          );
                        });

                    } else {
                      this.form.removeControl(`actions_response_dropdown`, { emitEvent: false });
                    }
                  });
              }

              this.form.addControl(`actions_${o?.name}`, new FormControl({ value: null, disabled: false }), { emitEvent: false });
            });
          });
        });
    } else {
      this.form.removeControl('actions', { emitEvent: false });

      Object.keys(this.form.controls)
        .filter(key => key.startsWith('actions_'))
        .forEach(key => this.form.removeControl(key, { emitEvent: false }));
    }

    this.projectsService.isAnyFormDirty
      .pipe(takeUntil(this.onDestroy))
      .subscribe((dirty) => {
        this.isFormDirty = dirty;
      });

    this.commonService.uploadedFilesInfo
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        const items = Array.isArray(data) ? data : [data];
        items.forEach((eachItem: any) => {
          if (eachItem?.['fileType'] === 'picture' || eachItem?.['fileType'] === 'document') {
            if (!eachItem?.tags?.length) {
              let pictures: any = this.form.get('pictures')?.value || '[]';
              pictures = JSON.parse(pictures);

              pictures.push(eachItem);
              this.form?.get('pictures')?.setValue(JSON.stringify(pictures), { emitEvent: true });
              this.mediaFiles?.push(eachItem);
            } else {
              let pictures: any = this.form.get('actions_pictures')?.value || '[]';
              pictures = JSON.parse(pictures);

              pictures.push(eachItem);
              this.form?.get('actions_pictures')?.setValue(JSON.stringify(pictures), { emitEvent: true });
              this.mediaFiles?.push(eachItem);
            }
          }
        });
        setTimeout(() => this.projectsService.mediaData.next(this.mediaFiles));
      });

    this.form.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.projectsService.isAnyFormDirty.next(true);
      });

    this.projectsService.removeActionFieldsFromForm
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data) => {
        if (data) {
          Object.keys(this.form.controls)
            .filter(key => key.startsWith('actions_'))
            .forEach(key => this.form.removeControl(key, { emitEvent: false }));

          this.form.patchValue({
            actions: null,
          }, { emitEvent: false });
        }
      });

    // A new RFI opened with a link already chosen (the Drawings viewer's Add to). Dirty, so the save sends it.
    if (!this.data?.id && this.data?.map_feature_link) {
      this.form.patchValue({ map_feature_link: this.data.map_feature_link }, { emitEvent: false });
      this.form.get('map_feature_link')?.markAsDirty();
    }

    // The pin picker, the station dialog (emitSelectedLocation) and the
    // location card's click-to-edit all hand their result to this shared
    // subject. Patch only the keys the dialog actually sent.
    this.dailyReportService.selectedLocation
      .pipe(takeUntil(this.onDestroy))
      .subscribe(location => this.applyPickedLocation(location));

    this.store.select(projectDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(project => {
        this.project = project;
        this.updateStationRangeEnabled();
      });
    this.store.select(dailyReportLocationConfig)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(config => {
        this.locationConfig = config;
        this.updateStationRangeEnabled();
      });

    this.store.select(usersListInfo)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (data && data.list?.length > 0) {
          this.userOptions = [...data.list];
        }
      });

    this.store.select(rfiNo)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (data) {
          if (!this.data?.id) {
            this.form.patchValue({
              rfiNo: data,
            }, { emitEvent: false });
          } else {
            this.form.patchValue({
              rfiNo: this.data?.rfi_no,
            }, { emitEvent: false });
          }

        }
      });

    this.projectsService.changeView
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (Object.keys(data)?.length) {
          this.data = {};
          this.mediaFiles = [];

          this.data = this.workflowService.gateNextActions(data);

          if (this.data?.id) {
            this.creatorRole = this.data?.createdBy?.id === this.account?.id;

            this.store.dispatch(FetchLineageRequest({
              payload: {
                qp: {
                  start: 0,
                  total: 1000,
                  ex: `rfi-approval/${this.data?.id}`,
                  include: ['actor', 'state', 'attachments'],
                },
              },
            }));

            this.form.patchValue({
              rfiNo: this.data?.rfi_no,
              title: this.data?.title,
              need_by_date: this.data?.need_by_date,
              description: this.data?.description,
              cc_users: this.data?.cc_users?.map(u => u.value),
              reviewers: this.data?.reviewers?.map(u => u.value),
              ...readRemarkLocation(this.data),
            }, { emitEvent: false });
            this.form.get('map_feature_link')?.markAsPristine();

            if (this.data?.attachments?.length) {
              const mediaFiles = this.data?.attachments;
              this.mediaFiles = [...mediaFiles];
              this.form.get('pictures')?.setValue(JSON.stringify(mediaFiles), { emitEvent: false });
            }

            setTimeout(() => {
              this.projectsService.mediaData.next(this.mediaFiles);
            });

            if (this.data.status === 'closed' || this.data.status === 'submitted' || this.data.status === 'sent_for_review') {
              this.form.disable();
            } else {
              this.form.enable();
            }

            this.actionsOptions = this.data?.['next-actions']?.[0]?.actions?.map(o => {
              return {
                ...o,
                label: o?.options?.label,
                value: o?.state,
              };
            }) || [];

            if (this.role === 'project-manager') {
              if (this.creatorRole) {
                if (this.data?.status === 'submitted') {
                  this.displayAprovalNextActions = true;
                  this.displayNextActions = false;
                  this.form.controls?.['actions'].enable();
                } else if (this.data?.status === 'sent_for_review') {
                  if (this.data?.latest_lineage?.state?.name === 'rfi-reviewer-response') {
                    this.displayAprovalNextActions = true;
                    this.displayNextActions = false;
                    this.form.controls?.['actions'].enable();
                  } else {
                    this.displayNextActions = false;
                  }
                } else {
                  this.displayNextActions = !!this.data?.['next-actions']?.[0]?.actions?.length;
                }
              } else {
                this.form.controls?.['actions'].enable();
                this.displayNextActions = !!this.data?.['next-actions']?.[0]?.actions?.length;
              }
            } else if (this.role?.includes('contractor')) {
              this.displayNextActions = !!this.data?.['next-actions']?.[0]?.actions?.length;
            } else if (this.role === 'reviewer') {
              this.displayAprovalNextActions = true;
              this.form.controls?.['actions'].enable();
              this.displayNextActions = !!this.data?.['next-actions']?.[0]?.actions?.length;
            }
          }

          this.projectsService.isAnyFormDirty.next(false);
        }
      });

    this.fetchUsersList();

    this.store.select(rfiItemDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (Object.keys(data)?.length) {
          this.data = {};
          this.mediaFiles = [];

          this.data = this.workflowService.gateNextActions(data);

          if (this.data?.id) {
            this.creatorRole = this.data?.createdBy?.id === this.account?.id;

            this.store.dispatch(FetchLineageRequest({
              payload: {
                qp: {
                  start: 0,
                  total: 1000,
                  ex: `rfi-approval/${this.data?.id}`,
                  include: ['actor', 'state', 'attachments'],
                },
              },
            }));

            this.form.patchValue({
              rfiNo: this.data?.rfi_no,
              title: this.data?.title,
              need_by_date: this.data?.need_by_date,
              description: this.data?.description,
              cc_users: this.data?.cc_users?.map(u => u.value),
              reviewers: this.data?.reviewers?.map(u => u.value),
              ...readRemarkLocation(this.data),
            }, { emitEvent: false });
            this.form.get('map_feature_link')?.markAsPristine();

            if (this.data?.attachments?.length) {
              const mediaFiles = this.data?.attachments;
              this.mediaFiles = [...mediaFiles];
              this.form.get('pictures')?.setValue(JSON.stringify(mediaFiles), { emitEvent: false });
            }

            setTimeout(() => {
              this.projectsService.mediaData.next(this.mediaFiles);
            });

            if (this.data.status === 'closed' || this.data.status === 'submitted' || this.data.status === 'sent_for_review') {
              this.form.disable();
            } else {
              this.form.enable();
            }

            this.actionsOptions = this.data?.['next-actions']?.[0]?.actions?.map(o => {
              return {
                ...o,
                label: o?.options?.label,
                value: o?.state,
              };
            }) || [];

            if (this.role === 'project-manager') {
              if (this.creatorRole) {
                if (this.data?.status === 'submitted') {
                  this.displayAprovalNextActions = true;
                  this.displayNextActions = false;
                  this.form.controls?.['actions'].enable();
                } else if (this.data?.status === 'sent_for_review') {
                  this.displayAprovalNextActions = true;
                  this.displayNextActions = false;
                  this.form.controls?.['actions'].enable();
                } else {
                  this.displayNextActions = !!this.data?.['next-actions']?.[0]?.actions?.length;
                }
              } else {
                this.form.controls?.['actions'].enable();
                this.displayNextActions = !!this.data?.['next-actions']?.[0]?.actions?.length;
              }
            } else if (this.role?.includes('contractor')) {
              this.displayNextActions = !!this.data?.['next-actions']?.[0]?.actions?.length;
            } else if (this.role === 'reviewer') {
              this.displayAprovalNextActions = true;
              this.form.controls?.['actions'].enable();
              this.displayNextActions = !!this.data?.['next-actions']?.[0]?.actions?.length;
            }
          }

          this.projectsService.isAnyFormDirty.next(false);
        }
      });

    this.store.select(lineageList)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (Array.isArray(data) && data.length && this.data?.id) {
          this.finalResponse = data.filter(o => o?.state?.name === 'rfi-close');
          if (this.finalResponse?.length) {
            const attachments = Array.isArray(this.finalResponse[0]?.attachments)
              ? this.finalResponse[0].attachments
              : [];

            this.mediaFiles = [
              ...this.mediaFiles,
              ...attachments.filter(a => !this.mediaFiles.some(m => m.id === a.id)),
            ];

            setTimeout(() => {
              this.projectsService.mediaData.next(this.mediaFiles);
            });
          }

          this.responseList = data.filter((o => o?.state?.name === 'rfi-reviewer-response')).map(o => {
            return {
              ...o,
              label: `${o.actor.first_name} ${o.actor.last_name}`,
              value: o.id,
              response: o?.input?.formValues?.comments,
            };
          });
        }
      });
  }

  ngAfterViewInit() {
    this.projectsService.isAnyFormDirty.next(false);
    if (this.tabs) {
      this.tabs._handleClick = this.onTabChange?.bind(this);
    }
  }

  checkForErrors(currentField?: string) {
    this.errorMessages = {
      ...this.errorMessages,
      ...(this.commonService.checkFormValidation(this.form, this.errorMessageMap, currentField)),
    };
  }

  save() {
    if (this.form.invalid) {
      this.checkForErrors();
      return;
    }

    const values = this.form.getRawValue();
    const attachments = JSON?.parse(values?.pictures).map(o => o.id);

    const payload = {
      title: values?.title,
      description: values?.description,
      need_by_date: moment.isMoment(values?.need_by_date)
        ? values?.need_by_date.format('YYYY-MM-DD')
        : values?.need_by_date,
      projectId: this.projectId,
      attachments,
      ...this.locationPayload(values),
    };

    if (this.data.id) {
      payload['id'] = this.data.id;
    }

    const tempPayload = {
      payload,
      type: 'save',
      ...this.requestFlags(),
    };

    this.store.dispatch(CreateRFIRequest({ payload: tempPayload }));

    if (this.data?.id) {
      this.analyticsService.capture('rfi_updated', {
        project_id: this.projectId,
        action: 'save',
      });
    } else {
      this.analyticsService.capture('rfi_created', {
        project_id: this.projectId,
        action: 'save',
      });
    }
  }

  submit() {
    if (this.form.invalid) {
      this.checkForErrors();
      return;
    }

    const values = this.form.getRawValue();

    const attachments = JSON?.parse(values?.pictures).map(o => o.id);

    const payload = {
      title: values?.title,
      description: values?.description,
      reviewers: values?.reviewers,
      need_by_date: moment.isMoment(values?.need_by_date)
        ? values?.need_by_date.format('YYYY-MM-DD')
        : values?.need_by_date,
      projectId: this.projectId,
      attachments,
      ...this.locationPayload(values),
    };

    const tempPayload = {
      payload,
      type: 'submit',
      ...this.requestFlags(),
    };

    if (this.data.id) {
      payload['id'] = this.data.id;
    }

    this.store.dispatch(CreateRFIRequest({ payload: tempPayload }));

    if (this.data?.id) {
      this.analyticsService.capture('rfi_updated', {
        project_id: this.projectId,
        action: 'submit',
      });
    } else {
      this.analyticsService.capture('rfi_created', {
        project_id: this.projectId,
        action: 'submit',
      });
    }
  }

  fetchUsersList(): void {
    const payload = {
      start: 0,
      total: 1000,
      include: ['roles'],
      projectId: this.projectId,
    };

    if (this.projectId) {
      this.store.dispatch(FetchUsersListRequest({ payload }));
    }
  }

  onFileSelected(event: any) {
    const files: FileList = event.target.files;
    const imageFormData = new FormData();
    const videoFiles: File[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files.item(i);

      if (classifyFile(file) === 'video') {
        videoFiles.push(file);
      } else {
        imageFormData.append('inputFiles', file);
      }
    }

    if (imageFormData.has('inputFiles')) {
      this.store.dispatch(UploadFileRequest({ payload: imageFormData }));
    }

    if (videoFiles.length > 0) {
      videoFiles.forEach((videoFile: File) => {
        const videoElement = document.createElement('video');
        const reader = new FileReader();

        reader.onloadend = () => {
          videoElement.src = reader.result as string;

          videoElement.onloadedmetadata = () => {
            const videoDuration = videoElement.duration;
            const maxDuration = this.limits.video_size_in_secs;

            if (videoDuration > maxDuration) {
              this.commonService.notification(
                `Video duration is ${videoDuration} seconds, should be less than ${maxDuration} seconds.`,
                'success',
              );
            } else {
              this.store.dispatch(GetVideoIdRequest({ payload: videoFile }));
            }
          };
        };

        reader.readAsDataURL(videoFile);
      });
    }

    this.fileInputRef.nativeElement.value = '';
  }

  deletePicture(pic) {
    if (pic.fileType === 'picture' || pic.fileType === 'document') {
      let pictures = this.getPictures();

      pictures = pictures?.filter(o => o.id !== pic.id);
      this.mediaFiles = this.mediaFiles?.filter(o => o.id !== pic.id);
      this.form?.get('pictures')?.setValue(JSON.stringify(pictures));
    }

    if (pic.fileType === 'video') {
      let videos = this.getVideos();

      videos = videos?.filter(o => o.id !== pic.id);
      this.mediaFiles = this.mediaFiles?.filter(o => o.id !== pic.id);
      this.form?.get('videos')?.setValue(JSON.stringify(videos));
      this.videoToBeDeletedFromMuxDb.push(pic.id);
    }

    setTimeout(() => this.projectsService.mediaData.next(this.mediaFiles));
  }

  getPictures() {
    const pictures = this.form.controls['pictures']?.value || '[]';
    return JSON.parse(pictures);
  }

  getVideos() {
    const videos = this.form.controls['videos']?.value || '[]';
    return JSON.parse(videos);
  }

  onTabChange(event: MatTabChangeEvent) {
    const label = event?.['textLabel']?.toLowerCase()?.replace(/\s+/g, '-');

    if (this.isFormDirty) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        disableClose: true,
        data: new ConfirmDialogModel(
          'Unsaved changes',
          `There are unsaved changes.<br/>Are you sure you want to discard?`,
        ),
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { subTab: label },
            queryParamsHandling: 'merge',
          }).then(() => {
            this.projectsService.isAnyFormDirty.next(false);
          });
        }
      });
    } else {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { subTab: label },
        queryParamsHandling: 'merge',
      });
    }
  }

  executeState(action) {
    if (action.state === 'rfi-submit') {
      if (this.isFormDirty) {
        if (this.form.invalid) {
          this.checkForErrors();
          return;
        }

        const values = this.form.getRawValue();

        const attachments = JSON?.parse(values?.pictures).map(o => o.id);

        const payload = {
          title: values?.title,
          description: values?.description,
          reviewers: values?.reviewers,
          need_by_date: moment.isMoment(values?.need_by_date)
            ? values?.need_by_date.format('YYYY-MM-DD')
            : values?.need_by_date,
          projectId: this.projectId,
          attachments,
          ...this.locationPayload(values),
        };

        const tempPayload = {
          payload,
          type: 'submit',
          ...this.requestFlags(),
        };

        if (this.data.id) {
          payload['id'] = this.data.id;
        }

        this.store.dispatch(CreateRFIRequest({ payload: tempPayload }));
      } else {
        if (!action?.options?.formInputs?.length) {
          const { wf, ex, state } = action;

          const qp = { wf, ex, state };

          const payload = {
            projectId: this.projectId,
            qp,
            input: {
              id: this.data?.id,
            },
          };

          const tempPayload = {
            type: 'rfis',
            payload,
          };

          this.store.dispatch(ExecuteWorkflowActionStateRequest({ payload: tempPayload }));
        }
      }
    } else {
      if (!action?.options?.formInputs?.length) {
        const { wf, ex, state } = action;

        const qp = { wf, ex, state };

        const payload = {
          projectId: this.projectId,
          qp,
          input: {
            id: this.data?.id,
          },
        };

        const tempPayload = {
          type: 'rfis',
          payload,
        };

        this.store.dispatch(ExecuteWorkflowActionStateRequest({ payload: tempPayload }));
      }
    }
  }

  deleteActionPicture(pic) {
    if (pic.fileType === 'picture' || pic.fileType === 'document') {
      let pictures = this.getActionPictures();

      pictures = pictures?.filter(o => o.id !== pic.id);
      this.mediaFiles = this.mediaFiles?.filter(o => o.id !== pic.id);
      this.form.controls['actions_pictures']?.setValue(JSON.stringify(pictures));
      setTimeout(() => {
        this.projectsService.mediaData.next(this.mediaFiles);
      });
    }
  }

  getActionPictures() {
    const pictures = this.form.controls['actions_pictures']?.value || '[]';
    return JSON.parse(pictures);
  }

  execute() {

    const { wf, ex, state } = this.selectedAction[0];

    const qp = { wf, ex, state };

    const fvs = this.form.getRawValue();

    let tempPics = [];
    if (fvs?.actions_pictures) {
      tempPics = JSON?.parse(fvs?.actions_pictures)?.map(o => o?.id);
    }

    const payload = {
      projectId: this.projectId,
      qp,
      input: {
        formValues: {
          final_response: fvs?.actions_final_response,
          _reviewers: fvs?.actions__reviewers,
          comments: fvs?.actions_comments,
          attachments: tempPics,
        },
        id: this.data?.id,
      },
    };

    const tempPayload = {
      type: 'rfis',
      payload,
    };

    this.store.dispatch(ExecuteWorkflowActionStateRequest({ payload: tempPayload }));
  }

  onToggle(event: MatSlideToggleChange) {
    this.form.controls['actions_select_from_previous_responses'].setValue(event.checked);
  }

  /**
   * Download a PDF of this RFI. `includeDiscussions=true` appends the workflow
   * timeline. Resolves the 'rfi_detail' report template at project or org scope;
   * falls back to the system default for new orgs.
   */
  get canDeleteRfi(): boolean {
    return !!this.data?.id && this.data.status === 'draft' && this.ability.can('delete', subject('RFI', {
      ...this.data,
      project_id: this.data.project?.id || this.projectId,
      created_by: this.data.createdBy?.id || this.data.created_by,
    }));
  }

  deleteRfi(): void {
    if (!this.canDeleteRfi || this.deletingRfi) {
      return;
    }
    const id = this.data.id;
    this.deletingRfi = true;
    this.dialog.open(ConfirmDialogComponent, {
      width: '460px',
      data: new ConfirmDialogModel(
        'Delete draft RFI',
        'Are you sure you want to delete this draft RFI? This cannot be undone.',
        'Delete',
        'Cancel',
      ),
    }).afterClosed().pipe(
      filter(confirmed => confirmed === true),
      switchMap(() => this.projectsService.deleteRFIItem({ id })),
      takeUntil(this.onDestroy),
      finalize(() => this.deletingRfi = false),
    ).subscribe({
      next: () => {
        this.store.dispatch(FetchRFIsListRequest({
          payload: { qp: { projectId: this.projectId, start: 0, total: 1000 } },
        }));
        this.snackBar.open('Draft RFI deleted', '', { duration: 3000 });
        this.router.navigate([], {
          queryParams: { rifId: null },
          queryParamsHandling: 'merge',
        });
        this.commonService.clearRowSelection.next();
        this.projectsService.closeRightPanel.next();
      },
      error: () => {
        this.snackBar.open(
          'Unable to delete RFI. It may no longer be a draft, or you may not have permission.',
          '',
          { duration: 5000 },
        );
      },
    });
  }

  async downloadRfiDetail(includeDiscussions: boolean): Promise<void> {
    if (this.downloadingReport || !this.data?.id || !this.projectId) {
      return;
    }
    this.downloadingReport = true;
    try {
      const report = await this.reportService.resolveEntityReport(this.projectId, 'rfi_detail');
      if (!report) {
        this.snackBar.open(
          'No RFI detail report template is configured. Please contact your administrator.',
          '',
          { duration: 5000 },
        );
        return;
      }
      await this.reportService.downloadReport(
        report,
        null,
        { id: this.projectId },
        {
          rfiId: this.data.id,
          includeDiscussions,
          attachments: true,
        },
        true,
        false,
      );
      this.snackBar.open('Report downloaded successfully', '', { duration: 3000 });
    } catch {
      this.snackBar.open('Failed to download report', '', { duration: 5000 });
    } finally {
      this.downloadingReport = false;
    }
  }

  /** A new RFI opened from the Drawings viewer's Add to stays on the sheet after the save (W20). */
  private requestFlags(): { stayInPlace?: boolean } {
    return !this.data?.id && this.data?.stayInPlace ? { stayInPlace: true } : {};
  }

  /** Same condition as the report editor's `showLocationSection()`, plus a sheet-only link (no pin). */
  get hasLocation(): boolean {
    const values = this.form.getRawValue();
    return hasRemarkLocation(values) || !!values?.map_feature_link?.sheet;
  }

  /** Location fields are editable while the RFI itself is (draft / new). */
  get locationEditable(): boolean {
    return this.form.enabled && (!this.data?.id || this.data?.status === 'draft');
  }

  /** The location card's "Clear": null all four fields and the link, like issue-details-form. */
  deleteLocation(): void {
    if (!this.locationEditable) return;
    const linkControl = this.form.get('map_feature_link');
    if (linkControl?.value) linkControl.markAsDirty();
    this.form.patchValue({ latitude: null, longitude: null, location: null, station_range: null, map_feature_link: null });
    this.form.markAsDirty();
  }

  /**
   * The four location keys plus the link (tri-state). A link that is null and untouched is omitted, so a project
   * without design layers sends today's payload; a removed or cleared link sends null.
   */
  private locationPayload(values: LooseRecord) {
    const removed = values?.map_feature_link == null && !!this.form.get('map_feature_link')?.dirty;
    const payload = remarkLocationPayload(values, removed);
    if (values?.map_feature_link == null && !removed) delete payload.map_feature_link;
    return payload;
  }

  /**
   * "+" > "Add location". Seeded from the RFI's own pin when it has one, else
   * the device location, else the project. The free-text label box is part of
   * the picker only in station-range mode (`enableLocationSection`), as for
   * diary remarks.
   */
  async openLocationPicker(initialTab?: 'drawing'): Promise<void> {
    if (!this.locationEditable) return;
    const values = this.form.getRawValue();
    const seed = hasRemarkLocation({ latitude: values.latitude, longitude: values.longitude })
      ? { latitude: values.latitude, longitude: values.longitude }
      : null;
    const fallback = seed || this.fallbackLocation(await firstValueFrom(this.store.select(currentLocation).pipe(take(1))));
    // The design-context picker on projects with published layers, else today's Google picker. Both hand
    // their result to `dailyReportService.selectedLocation` (see ngOnInit).
    await this.locationPickerLauncher.open({
      projectId: this.projectId || undefined,
      latitude: fallback.latitude as number | string | null,
      longitude: fallback.longitude as number | string | null,
      enableLocationSection: this.stationRangeEnabled,
      showIncludeGpsLocationCheckbox: false,
      locationText: values.location || '',
      existingLink: values.map_feature_link ?? null,
      recordLabel: 'RFI',
      ...(initialTab ? { initialTab } : {}),
    }).catch(error => console.error('Location picker failed to open', error));
  }

  /**
   * "+" > "Station range". Same dialog config as the diary remark / report
   * toolbar, pre-filled from the current range. The dialog hands its result to
   * `dailyReportService.selectedLocation` (see ngOnInit).
   */
  openStationRangeDialog(): void {
    if (!this.locationEditable || !this.stationRangeEnabled) return;
    const sr = this.form.get('station_range')?.value || {};
    const from = sr.from || {};
    const to = sr.to || {};
    this.dialog.open(StationRangeComponent, {
      disableClose: true,
      width: '1200px',
      height: '620px',
      maxWidth: 'calc(100vw - 48px)',
      maxHeight: 'calc(100vh - 48px)',
      panelClass: 'station-range-dialog-panel',
      data: {
        emitSelectedLocation: true,
        form: new FormGroup({
          alignment: new FormControl(sr.alignment || null),
          stationRangeFromStation: new FormControl(from.station || null),
          stationRangeFromOffsetType: new FormControl(from.offsetType ?? from.offset_type ?? null),
          stationRangeFromOffsetDistance: new FormControl(from.offsetDistance ?? from.offset_distance ?? null),
          stationRangeToStation: new FormControl(to.station || null),
          stationRangeToOffsetType: new FormControl(to.offsetType ?? to.offset_type ?? null),
          stationRangeToOffsetDistance: new FormControl(to.offsetDistance ?? to.offset_distance ?? null),
        }),
      },
    });
  }

  /** Picker result -> the form. Patches only the keys the dialog sent. */
  private applyPickedLocation(location: unknown): void {
    if (!this.locationEditable) return;
    const patch = normalizeLocationPayload(location);
    if (!Object.keys(patch).length) return;
    this.form.patchValue(patch);
    if ('map_feature_link' in patch) this.form.get('map_feature_link')?.markAsDirty();
    this.form.markAsDirty();
  }

  private updateStationRangeEnabled(): void {
    this.stationRangeEnabled = resolveDailyReportLocationMode(this.project, this.locationConfig) === 'station_range'
      || projectHasAlignmentGeometry(this.project);
    this.addMenuOptions = [
      this.attachmentsMenuOption,
      this.locationMenuOption,
      ...(this.hasPublishedSheets ? [this.markOnDrawingMenuOption] : []),
      ...(this.stationRangeEnabled ? [this.stationRangeMenuOption] : []),
    ];
  }

  /** Device location, else the project's pin. RFIs are project-level, so no site fallback. */
  private fallbackLocation(location: LooseRecord | null): LooseRecord {
    if (location?.latitude && location?.longitude) return location;
    if (this.project?.latitude && this.project?.longitude) {
      return { ...(location || {}), latitude: this.project.latitude, longitude: this.project.longitude };
    }
    return location || {};
  }

  ngOnDestroy() {
    // W74 review I1: torn down without a save or the drawer's confirm, the unsaved flag must not outlive the form
    this.projectsService.isAnyFormDirty.next(false);
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
